import { useEffect, useRef, useState } from 'react'
import { ApiClientError } from '../../services/apiClient'
import {
  ALLOWED_IMAGE_TYPES,
  configClient,
  LOGO_MAX_BYTES,
  SIGNATURE_MAX_BYTES,
  type ConfigUpdatePayload,
  type TenantConfig,
} from '../../services/configClient'
import { compressUploadImage } from '../../services/imageProcessing'
import { ProtectedImage } from '../../components/ProtectedImage'

/**
 * Configuración del tenant (§9 SRS).
 *
 * Tres secciones independientes que se guardan por separado para que cada cambio
 * sea atómico (CONF-01):
 *   1. Datos básicos + folio (company_name, prefix, current_sequence, page_size).
 *   2. Toggles de PDF (signature_enabled, responsible_name_enabled).
 *   3. Logo y firma (uploads multipart separados).
 *
 * Preview del próximo folio en vivo: usa folio_prefix + (current_sequence + 1)
 * que devuelve el backend en `next_folio_preview`, y recomputa en el cliente al editar.
 */
export function ConfiguracionPage() {
  const [config, setConfig] = useState<TenantConfig | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    configClient
      .show()
      .then((res) => setConfig(res.config))
      .catch((err) =>
        setLoadError(
          err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar la configuración.',
        ),
      )
  }, [])

  if (loadError) {
    return (
      <div className="content-stack">
        <h1>Configuración</h1>
        <p className="error">{loadError}</p>
      </div>
    )
  }

  if (!config) {
    return (
      <div className="content-stack">
        <h1>Configuración</h1>
        <p className="muted">Cargando…</p>
      </div>
    )
  }

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Configuración</h1>
          <span className="muted">{config.company_name}</span>
        </div>
      </header>

      <BasicSection config={config} onSaved={setConfig} />
      <PdfTogglesSection config={config} onSaved={setConfig} />
      <ImagesSection config={config} onSaved={setConfig} />
    </div>
  )
}

// ─── Sección 1: Datos básicos + folio ───────────────────────────────────────
function BasicSection({
  config,
  onSaved,
}: {
  config: TenantConfig
  onSaved: (c: TenantConfig) => void
}) {
  const [companyName, setCompanyName] = useState(config.company_name)
  const [folioPrefix, setFolioPrefix] = useState(config.folio_prefix)
  const [currentSequence, setCurrentSequence] = useState(String(config.current_sequence))
  const [pageSize, setPageSize] = useState(config.page_size)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)

  // Preview del próximo folio en vivo (sin tocar BD).
  const seqNum = Number.parseInt(currentSequence, 10)
  const nextSeq = Number.isFinite(seqNum) ? seqNum + 1 : config.current_sequence + 1
  const folioPreview = `${folioPrefix}${String(Math.max(0, nextSeq)).padStart(5, '0')}`

  const dirty =
    companyName !== config.company_name ||
    folioPrefix !== config.folio_prefix ||
    Number.parseInt(currentSequence, 10) !== config.current_sequence ||
    pageSize !== config.page_size

  async function handleSave() {
    setError(null)
    setSavedMessage(null)
    setSaving(true)
    try {
      const payload: ConfigUpdatePayload = {
        company_name: companyName.trim(),
        folio_prefix: folioPrefix,
        current_sequence: Number.parseInt(currentSequence, 10),
        page_size: pageSize,
      }
      const res = await configClient.update(payload)
      onSaved(res.config)
      setSavedMessage('Guardado.')
      setTimeout(() => setSavedMessage(null), 2500)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card">
      <h3>Datos básicos y folio</h3>
      <div className="form-grid">
        <label className="field">
          <span>Nombre de la empresa</span>
          <input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            maxLength={255}
            disabled={saving}
          />
        </label>

        <div className="field-row">
          <label className="field">
            <span>Prefijo de folio <em className="muted">(máx. 10 caracteres)</em></span>
            <input
              value={folioPrefix}
              onChange={(e) => setFolioPrefix(e.target.value)}
              maxLength={10}
              placeholder="DEMO-"
              className="mono"
              disabled={saving}
            />
          </label>
          <label className="field">
            <span>Consecutivo actual <em className="muted">(último folio emitido)</em></span>
            <input
              type="number"
              value={currentSequence}
              onChange={(e) => setCurrentSequence(e.target.value)}
              min={0}
              disabled={saving}
              className="mono"
            />
          </label>
        </div>

        <div className="config-preview">
          <small className="muted">Siguiente folio</small>
          <div className="mono config-preview__value">{folioPreview}</div>
        </div>

        <label className="field">
          <span>Tamaño de hoja del PDF</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(e.target.value as 'LETTER' | 'A4')}
            disabled={saving}
          >
            <option value="LETTER">Carta (Letter)</option>
            <option value="A4">A4</option>
          </select>
        </label>

        {error && <p className="error">{error}</p>}
        {savedMessage && <p className="info">{savedMessage}</p>}

        <div className="modal__actions">
          <button
            type="button"
            className="primary-btn"
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </section>
  )
}

// ─── Sección 2: Toggles del PDF ─────────────────────────────────────────────
function PdfTogglesSection({
  config,
  onSaved,
}: {
  config: TenantConfig
  onSaved: (c: TenantConfig) => void
}) {
  const [signatureEnabled, setSignatureEnabled] = useState(config.signature_enabled)
  const [clientSignatureEnabled, setClientSignatureEnabled] = useState(config.client_signature_enabled)
  const [responsibleEnabled, setResponsibleEnabled] = useState(config.responsible_name_enabled)
  // Encabezados
  const [showCompanyName, setShowCompanyName] = useState(config.show_company_name)
  const [showFolio, setShowFolio] = useState(config.show_folio)
  // Datos básicos
  const [showClient, setShowClient] = useState(config.show_client)
  const [showRfc, setShowRfc] = useState(config.show_rfc)
  const [showAddress, setShowAddress] = useState(config.show_address)
  const [showSystem, setShowSystem] = useState(config.show_system)
  const [showBuilding, setShowBuilding] = useState(config.show_building)
  const [showOrder, setShowOrder] = useState(config.show_order)
  const [showCity, setShowCity] = useState(config.show_city)
  const [showDates, setShowDates] = useState(config.show_dates)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)

  const dirty =
    signatureEnabled !== config.signature_enabled ||
    clientSignatureEnabled !== config.client_signature_enabled ||
    responsibleEnabled !== config.responsible_name_enabled ||
    showCompanyName !== config.show_company_name ||
    showFolio !== config.show_folio ||
    showClient !== config.show_client ||
    showRfc !== config.show_rfc ||
    showAddress !== config.show_address ||
    showSystem !== config.show_system ||
    showBuilding !== config.show_building ||
    showOrder !== config.show_order ||
    showCity !== config.show_city ||
    showDates !== config.show_dates

  async function handleSave() {
    setError(null)
    setSavedMessage(null)
    setSaving(true)
    try {
      const res = await configClient.update({
        signature_enabled: signatureEnabled,
        client_signature_enabled: clientSignatureEnabled,
        responsible_name_enabled: responsibleEnabled,
        show_company_name: showCompanyName,
        show_folio: showFolio,
        show_client: showClient,
        show_rfc: showRfc,
        show_address: showAddress,
        show_system: showSystem,
        show_building: showBuilding,
        show_order: showOrder,
        show_city: showCity,
        show_dates: showDates,
      })
      onSaved(res.config)
      setSavedMessage('Guardado.')
      setTimeout(() => setSavedMessage(null), 2500)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card">
      <h3>Opciones del PDF</h3>
      <div className="form-grid">

        <p className="field-group-label muted"><strong>Firma</strong></p>
        <ToggleRow
          label="Incluir firma del responsable en el PDF"
          help="Solo se incluye si hay imagen de firma cargada abajo."
          checked={signatureEnabled}
          onChange={setSignatureEnabled}
          disabled={saving}
        />
        <ToggleRow
          label="Imprimir nombre del responsable"
          help="Aparece bajo la sección de firma."
          checked={responsibleEnabled}
          onChange={setResponsibleEnabled}
          disabled={saving}
        />
        <ToggleRow
          label="Solicitar firma del cliente al cerrar reporte"
          help="Al cerrar un reporte aparecerá un modal para dibujar la firma del cliente, que se incluirá en el PDF."
          checked={clientSignatureEnabled}
          onChange={setClientSignatureEnabled}
          disabled={saving}
        />

        <p className="field-group-label muted"><strong>Encabezados</strong></p>
        <ToggleRow
          label="Nombre de la empresa"
          checked={showCompanyName}
          onChange={setShowCompanyName}
          disabled={saving}
        />
        <ToggleRow
          label="Folio"
          checked={showFolio}
          onChange={setShowFolio}
          disabled={saving}
        />

        <p className="field-group-label muted"><strong>Datos básicos del reporte</strong></p>
        <ToggleRow
          label="Cliente"
          checked={showClient}
          onChange={setShowClient}
          disabled={saving}
        />
        <ToggleRow
          label="RFC"
          checked={showRfc}
          onChange={setShowRfc}
          disabled={saving}
        />
        <ToggleRow
          label="Domicilio"
          checked={showAddress}
          onChange={setShowAddress}
          disabled={saving}
        />
        <ToggleRow
          label="Sistema"
          checked={showSystem}
          onChange={setShowSystem}
          disabled={saving}
        />
        <ToggleRow
          label="Edificio / Instalación"
          checked={showBuilding}
          onChange={setShowBuilding}
          disabled={saving}
        />
        <ToggleRow
          label="Orden"
          checked={showOrder}
          onChange={setShowOrder}
          disabled={saving}
        />
        <ToggleRow
          label="Ciudad"
          checked={showCity}
          onChange={setShowCity}
          disabled={saving}
        />
        <ToggleRow
          label="Fecha de Inicio y Fecha Fin"
          checked={showDates}
          onChange={setShowDates}
          disabled={saving}
        />

        {error && <p className="error">{error}</p>}
        {savedMessage && <p className="info">{savedMessage}</p>}

        <div className="modal__actions">
          <button
            type="button"
            className="primary-btn"
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </section>
  )
}

// ─── Sección 3: Logo + Firma (uploads) ──────────────────────────────────────
function ImagesSection({
  config,
  onSaved,
}: {
  config: TenantConfig
  onSaved: (c: TenantConfig) => void
}) {
  return (
    <section className="card">
      <h3>Logotipo y firma</h3>
      <div className="image-config-grid">
        <ImageUploader
          label="Logotipo de la empresa"
          help="Se mostrará en el encabezado del PDF. PNG, JPG o WEBP. Máx. 2 MB."
          path={config.logo_path}
          maxBytes={LOGO_MAX_BYTES}
          onUpload={async (file) => {
            const res = await configClient.uploadLogo(file)
            onSaved(res.config)
          }}
          onDelete={async () => {
            const res = await configClient.deleteLogo()
            onSaved(res.config)
          }}
        />

        <ImageUploader
          label="Imagen de firma"
          help="Preferiblemente PNG con fondo transparente. Máx. 1 MB."
          path={config.signature_path}
          maxBytes={SIGNATURE_MAX_BYTES}
          onUpload={async (file) => {
            const res = await configClient.uploadSignature(file)
            onSaved(res.config)
          }}
          onDelete={async () => {
            const res = await configClient.deleteSignature()
            onSaved(res.config)
          }}
        />
      </div>
    </section>
  )
}

// ─── Helpers UI ──────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  help,
  checked,
  onChange,
  disabled,
}: {
  label: string
  help?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className="toggle-row">
      <div className="toggle-row__text">
        <strong>{label}</strong>
        {help && <small className="muted">{help}</small>}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="toggle-switch"
      />
    </label>
  )
}

function ImageUploader({
  label,
  help,
  path,
  maxBytes,
  onUpload,
  onDelete,
}: {
  label: string
  help: string
  path: string | null
  maxBytes: number
  onUpload: (file: File) => Promise<void>
  onDelete: () => Promise<void>
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleFile(file: File) {
    setError(null)
    if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
      setError('Formato no permitido. Usa PNG, JPG o WEBP.')
      return
    }
    setUploading(true)
    try {
      const processed = await compressUploadImage(file, maxBytes / 1024 / 1024)
      if (processed.size > maxBytes) {
        setError(`El archivo excede el tamaño máximo (${Math.ceil(maxBytes / 1024 / 1024)} MB).`)
        return
      }
      await onUpload(processed)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo subir el archivo.')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function handleDelete() {
    setError(null)
    setDeleting(true)
    try {
      await onDelete()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo eliminar.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="image-uploader">
      <div className="image-uploader__header">
        <strong>{label}</strong>
        <small className="muted">{help}</small>
      </div>

      <div className="image-uploader__preview">
        {path ? (
          <ProtectedImage path={path} alt={label} className="image-uploader__img" />
        ) : (
          <span className="muted">Sin imagen cargada</span>
        )}
      </div>

      {error && <p className="error">{error}</p>}

      <div className="image-uploader__actions">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleFile(file)
          }}
        />
        <button
          type="button"
          className="primary-btn"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || deleting}
        >
          {uploading ? 'Subiendo…' : path ? 'Reemplazar' : 'Subir imagen'}
        </button>
        {path && (
          <button
            type="button"
            className="danger-btn"
            onClick={handleDelete}
            disabled={uploading || deleting}
          >
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </button>
        )}
      </div>
    </div>
  )
}
